import React, { useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, X, AlertCircle, Loader2, FileSpreadsheet, ChevronDown } from "lucide-react";

/**
 * ExcelTools — Canonical (iter30) row-based import/export for Units.
 *
 * Uses /admin/projects/{pid}/units/excel/{template,preview,apply,export}.
 * Match key = stable Etap Code / Block Code / Unit Code (option A).
 *
 * Props:
 *  - projectId
 *  - onAfterImport(): reload callback
 */
export default function ExcelTools({ projectId, onAfterImport }) {
  const inputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planFile, setPlanFile] = useState(null);
  const [exportMenu, setExportMenu] = useState(false);

  const downloadBlob = async (path, suggestedName) => {
    setDownloading(true);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setDownloading(false);
      setExportMenu(false);
    }
  };

  const downloadTemplate = () =>
    downloadBlob(`/admin/projects/${projectId}/units/excel/template`, "units-template.xlsx");
  const exportCanonical = () =>
    downloadBlob(
      `/admin/projects/${projectId}/units/excel/export?mode=canonical`,
      `units-canonical-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  const exportPretty = () =>
    downloadBlob(
      `/admin/projects/${projectId}/units/excel/export?mode=pretty`,
      `units-pretty-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/admin/projects/${projectId}/units/excel/preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPlan(r.data);
      setPlanFile(file);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setImporting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const apply = async () => {
    if (!planFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", planFile);
      const r = await api.post(
        `/admin/projects/${projectId}/units/excel/apply`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const t = r.data.applied || {};
      const parts = [];
      if (t.create_etaps) parts.push(`${t.create_etaps} etap${t.create_etaps === 1 ? "" : "s"}`);
      if (t.create_blocks) parts.push(`${t.create_blocks} block${t.create_blocks === 1 ? "" : "s"}`);
      if (t.create_units) parts.push(`${t.create_units} unit${t.create_units === 1 ? "" : "s"} created`);
      if (t.update_units) parts.push(`${t.update_units} unit${t.update_units === 1 ? "" : "s"} updated`);
      if (t.update_groups) parts.push(`${t.update_groups} group${t.update_groups === 1 ? "" : "s"} renamed`);
      toast.success(`Imported · ${parts.join(" · ") || "no changes"}`);
      setPlan(null);
      setPlanFile(null);
      onAfterImport?.();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="flex items-center gap-2 relative">
      <button
        onClick={downloadTemplate}
        disabled={downloading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50 disabled:opacity-50"
        data-testid="excel-template-btn"
        title="Download empty canonical template"
      >
        <FileSpreadsheet className="h-4 w-4" /> Template
      </button>
      <div className="relative">
        <button
          onClick={() => setExportMenu((v) => !v)}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50 disabled:opacity-50"
          data-testid="excel-export-btn"
        >
          <Download className="h-4 w-4" /> Export <ChevronDown className="h-3 w-3" />
        </button>
        {exportMenu && (
          <div
            className="absolute right-0 top-full mt-1 z-10 bg-white border border-zinc-200 rounded-md shadow-lg min-w-[240px] overflow-hidden"
            data-testid="excel-export-menu"
          >
            <button
              onClick={exportCanonical}
              className="block w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 border-b border-zinc-100"
              data-testid="excel-export-canonical"
            >
              <div className="font-medium">Canonical (re-importable)</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">Stable codes, one row per unit</div>
            </button>
            <button
              onClick={exportPretty}
              className="block w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50"
              data-testid="excel-export-pretty"
            >
              <div className="font-medium">Pretty (sales handout)</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">One sheet per Etap, color-coded</div>
            </button>
          </div>
        )}
      </div>
      <button
        onClick={() => inputRef.current?.click()}
        disabled={importing}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50 disabled:opacity-50"
        data-testid="excel-import-btn"
      >
        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
      </button>
      <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFile} />

      {plan && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => { setPlan(null); setPlanFile(null); }}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-xl p-6 max-h-[88vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="excel-import-dialog"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-xl">Import preview</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {planFile?.name} · matching by stable codes
                </div>
              </div>
              <button
                onClick={() => { setPlan(null); setPlanFile(null); }}
                className="p-1 hover:bg-zinc-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <Pill label="Etaps to create" value={plan.totals.create_etaps} testid="import-create-etaps" />
              <Pill label="Blocks to create" value={plan.totals.create_blocks} testid="import-create-blocks" />
              <Pill label="Groups renamed" value={plan.totals.update_groups} testid="import-update-groups" />
              <Pill label="Units to create" value={plan.totals.create_units} testid="import-create-units" />
              <Pill label="Units to update" value={plan.totals.update_units} testid="import-update-units" />
              <Pill
                label="Errors"
                value={plan.totals.errors}
                tone={plan.totals.errors ? "warn" : "ok"}
                testid="import-errors-count"
              />
            </div>

            {plan.errors?.length > 0 && (
              <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 max-h-44 overflow-auto" data-testid="import-errors">
                <div className="text-xs font-medium text-rose-900 flex items-center gap-1.5 mb-2">
                  <AlertCircle className="h-4 w-4" /> Errors ({plan.errors.length}) — fix and re-upload
                </div>
                <ul className="text-xs text-rose-900 space-y-1">
                  {plan.errors.map((e, i) => (
                    <li key={i}>
                      <span className="font-mono">{e.sheet} · row {e.row}</span> — {e.msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {plan.warnings?.length > 0 && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 max-h-32 overflow-auto" data-testid="import-warnings">
                <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5 mb-2">
                  <AlertCircle className="h-4 w-4" /> Warnings ({plan.warnings.length})
                </div>
                <ul className="text-xs text-amber-900 space-y-1">
                  {plan.warnings.slice(0, 30).map((w, i) => (
                    <li key={i}>
                      <span className="font-mono">{w.sheet} · row {w.row}</span> — {w.msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {plan.duplicate_codes?.length > 0 && (
              <div className="mt-3 text-xs text-rose-700">
                <strong>Duplicate Unit Codes in file:</strong> {plan.duplicate_codes.join(", ")}
              </div>
            )}

            {(plan.create_etaps?.length || plan.create_blocks?.length) > 0 && (
              <div className="mt-4">
                <div className="text-xs font-medium text-zinc-700 mb-2">Groups to create</div>
                <div className="rounded-md border border-zinc-200 overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 text-zinc-500">
                      <tr>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Code</th>
                        <th className="text-left px-3 py-2">Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.create_etaps.map((g) => (
                        <tr key={g.id} className="border-t border-zinc-100">
                          <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 text-[10px] uppercase rounded bg-indigo-100 text-indigo-800">Etap</span></td>
                          <td className="px-3 py-1.5 font-mono">{g.code}</td>
                          <td className="px-3 py-1.5">{g.name}</td>
                        </tr>
                      ))}
                      {plan.create_blocks.map((g) => (
                        <tr key={g.id} className="border-t border-zinc-100">
                          <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 text-[10px] uppercase rounded bg-sky-100 text-sky-800">Block</span></td>
                          <td className="px-3 py-1.5 font-mono">{g.code}</td>
                          <td className="px-3 py-1.5">{g.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-xs font-medium text-zinc-700 mb-2">
                Unit changes (showing first 200)
              </div>
              <div className="rounded-md border border-zinc-200 overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-500">
                    <tr>
                      <th className="text-left px-3 py-2">Action</th>
                      <th className="text-left px-3 py-2">Unit Code</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ...plan.create_units.map((u) => ({ ...u, _action: "create" })),
                      ...plan.update_units.map((u) => ({ ...u.patch, _action: "update" })),
                    ].slice(0, 200).map((u, i) => (
                      <tr key={i} className="border-t border-zinc-100">
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 text-[10px] uppercase rounded ${u._action === "create" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                            {u._action}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 font-mono">{u.code}</td>
                        <td className="px-3 py-1.5">{u.name}</td>
                        <td className="px-3 py-1.5">{u.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setPlan(null); setPlanFile(null); }}
                className="px-4 py-2 rounded-md border border-zinc-300 text-sm"
                data-testid="import-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={apply}
                disabled={importing || plan.totals.errors > 0}
                className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="import-apply-btn"
              >
                {importing && <Loader2 className="h-4 w-4 animate-spin" />} Apply import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pill({ label, value, tone = "ok", testid }) {
  const cls = tone === "warn"
    ? "border-rose-300 bg-rose-50 text-rose-900"
    : "border-zinc-200 bg-zinc-50 text-zinc-700";
  return (
    <div className={`rounded-md border ${cls} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="text-xl font-display mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}
